export const directionBFlows = {
  simplidevHomepage: [
    { label: "Requirement", kind: "workflow" },
    { label: "Plan / Design", kind: "workflow" },
    { label: "Implement", kind: "workflow" },
    { label: "Test", kind: "workflow" },
    { label: "Verify / Review", kind: "workflow" },
    { label: "Complete", kind: "workflow" },
  ],
  flagship: [
    { label: "Requirement", kind: "system" },
    { label: "Plan / Design", kind: "decision" },
    { label: "Implement", kind: "system" },
    { label: "Test / QA", kind: "gate" },
    { label: "Review / Rework", kind: "decision" },
    { label: "Delivery", kind: "system" },
  ],
  howIWork: [
    { label: "Discover", kind: "system" },
    { label: "Design", kind: "system" },
    { label: "Build", kind: "system" },
    { label: "Integrate", kind: "boundary" },
    { label: "Demo", kind: "system" },
    { label: "Feedback", kind: "decision" },
    { label: "Operate", kind: "system" },
    { label: "Iterate", kind: "system" },
  ],
  missionControl: [
    { label: "SimpliDev control plane", kind: "system" },
    { label: "Fit to SimpliGov", kind: "decision" },
    { label: "Customize / integrate", kind: "boundary" },
    { label: "Extend for SimpliDev", kind: "decision" },
    { label: "Operate", kind: "system" },
  ],
  governedAccess: [
    { label: "Developer identity", kind: "boundary" },
    { label: "Policy / gateway", kind: "gate" },
    { label: "Model + RAG", kind: "system" },
    { label: "MCP / tool broker", kind: "boundary" },
    { label: "Trace / audit", kind: "system" },
  ],
  simpliAI: [
    { label: "SimpliGov product", kind: "system" },
    { label: "Authenticated API", kind: "boundary" },
    { label: "Agent Framework", kind: "decision" },
    { label: "Grounded context", kind: "system" },
    { label: "Escalation / human review", kind: "decision" },
  ],
  workflow: [
    { label: "Document or form", kind: "system" },
    { label: "AI interpretation", kind: "decision" },
    { label: "Structured representation", kind: "system" },
    { label: "Conversion and normalization", kind: "boundary" },
    { label: "Validation", kind: "gate" },
    { label: "Designer", kind: "system" },
  ],
  mlb: [
    { label: "Connie / connectivity event", kind: "system" },
    { label: "MCP + provider tools", kind: "boundary" },
    { label: "Agentic orchestration", kind: "decision" },
    { label: "Network systems", kind: "system" },
    { label: "Triage / incident workflow", kind: "system" },
    { label: "Production outcome", kind: "system" },
  ],
  cms: [
    { label: "Authorized analyst", kind: "system" },
    { label: "Secure access", kind: "boundary" },
    { label: "Grounded retrieval", kind: "decision" },
    { label: "Cited response", kind: "system" },
    { label: "Evaluation and MLOps", kind: "gate" },
  ],
};

export const deliveryContext = [
  "Ambiguous requirements",
  "Evolving scope",
  "Existing enterprise systems",
  "Weekly stakeholder demos",
  "Feedback-driven iteration",
  "Production operations",
];

export const experienceEntries = [
  {
    dates: "Oct 2025 – Present",
    meta: "Full time · Remote",
    company: "SimpliGov",
    role: "Senior AI Engineer",
    qualifier: "Autonomous SDLC · durable workflow state · enterprise integration · production delivery",
    body: "Built and operated SimpliDev, a governed autonomous SDLC platform, and worked with product and stakeholders across Mission Control, its operational control plane; SimpliAI; and AI-assisted workflow generation. Built evaluation and observability systems that make AI behavior measurable and governable in production, alongside durable workflow state/history, OpenAI Agents SDK, Claude Agent SDK, MCP, Kubernetes, and KEDA.",
  },
  {
    dates: "Jul 2025 – Oct 2025",
    meta: "Consultant · Remote",
    company: "Major League Baseball",
    role: "Senior AI Engineer",
    qualifier: "Connie · production operations · cross-system integration",
    body: "Engineered production agentic-AI capabilities for Connie, MLB’s Connectivity Agent, with MCP/tool integrations, semantic provider resolution, maintenance and incident workflows, and reported 73% MTTR reduction with estimated $2.8M annual operational-efficiency savings.",
  },
  {
    dates: "Mar 2023 – May 2025",
    meta: "Full time · Remote",
    company: "eSimplicity",
    role: "Senior Full Stack / Cloud / GenAI Engineer",
    qualifier: "Federal healthcare · secure AI and data integration · regulated environment",
    body: "Worked directly with CMS stakeholders in requirements meetings and with product to clarify the need for secure, grounded access to large-scale data and analytics. Designed and implemented agent orchestration, MCP tool integration, secure RAG, citations, evaluation/MLOps, and cloud delivery for the $49.7M CMS Quality Data and Analytic Support (QDAS) program.",
  },
  {
    dates: "Feb 2022 – Mar 2023",
    meta: "Full time · Remote",
    company: "Wiser Solutions",
    role: "Principal Architect / Full Stack Engineer",
    qualifier: "product requirements · prototypes · architecture · AWS EKS",
    body: "Designed AI microservices, API gateway, and client onboarding architecture using AWS EKS, TypeScript, GraphQL, and NATS. Led product/stakeholder requirements discussions, built prototypes, and demonstrated working functionality.",
  },
  {
    dates: "Feb 2021 – Feb 2022",
    meta: "Full time · Remote",
    company: "Flowable",
    role: "Full Stack Developer",
    qualifier: "Client requirements · hands-on delivery · AWS EKS",
    body: "Worked directly with clients on requirements, process modeling, and implementation decisions, then built Flowable BPM case-management applications with Spring Boot, Node.js, React, BPMN 2.0, and decision tables for AWS EKS/Kubernetes deployment.",
  },
];

export const earlierFdeEntries = [
  {
    dates: "Aug 2019 – Feb 2021",
    company: "Delaware Life",
    role: "Full Stack Developer / DevOps Maintainer",
    pattern: "product/stakeholder requirements → full-stack engineering → DevOps",
    detail: "React delivery in a 15-member Agile/Scrum team; Kubernetes/GKE infrastructure and GitLab CI/CD ownership.",
  },
  {
    dates: "May 2016 – Jan 2019",
    company: "MHISSION / State of Utah",
    role: "Principal Architect / Lead Software Engineer",
    pattern: "State of Utah solution work → architecture → engineering → delivery",
    detail: "Built knowledge, decision, and process systems for State of Utah work using Camunda, Kafka, NLP, graph databases, blockchain, and microservices; led an eight-engineer team.",
  },
  {
    dates: "May 2010 – May 2016",
    company: "O’Reilly Auto Parts",
    role: "Lead Software Engineer / Architect",
    pattern: "product requirements → implementation → stakeholder demos → iteration",
    detail: "Angular, Spring Boot, React, Redux, Node.js, contract-management and product-promotion systems, B2B commerce, and AWS deployment.",
  },
  {
    dates: "Sep 2009 – Oct 2010",
    company: "Able Engineering",
    role: "Solutions Architect / Lead Software Engineer",
    pattern: "Requirements → proposal → design → prototype → implementation → production",
    detail: "Defined the Employee Evaluation System, built the prototype, led production implementation on SOA/OFBiz, and managed the development team.",
  },
  {
    dates: "Feb 1996 – Aug 2009",
    company: "Nexxus Systems",
    role: "Solutions Architect · Lead Software Engineer",
    pattern: "Platform engineering → advanced architecture → government delivery",
    phases: [
      {
        dates: "Aug 2004 – Aug 2009",
        role: "Solutions Architect / Lead Software Engineer",
        detail: "First commercial TV-Anytime implementation, BBC recognition, MPEG-7/21, personalization/recommendation, IPTV architecture, 12-engineer leadership, and contribution to technology covered by US 9,286,399 B2 (published as US 2015/0213129 A1).",
      },
      {
        dates: "Feb 1996 – Aug 2004",
        role: "Lead Software Engineer / Solutions Architect",
        detail: "Built Metasoft, the media-management product, and delivered semantic metadata, media-management, and content-delivery implementations for government departments including the IRS, DoD, and DoE.",
      },
    ],
  },
];

export const credentials = {
  education: [
    { degree: "Information Technology", school: "University of Phoenix · GPA 3.86" },
    { degree: "Information Technology", school: "Arizona State University" },
  ],
  verified: [
    { name: "Software Engineer", source: "HackerRank role", href: "https://www.hackerrank.com/certificates/58faf48b303a" },
    { name: "Machine Learning", source: "TestDome", href: "https://www.testdome.com/certificates/38284811f6cc46da8606669c0721b663" },
    { name: "Problem Solving, Intermediate", source: "HackerRank", href: "https://www.hackerrank.com/certificates/80d61360cde6" },
  ],
};

export const governedAccessControls = {
  control: [
    "Identity, roles, and team entitlements",
    "Approved model and tool catalog",
    "Policy-as-code, quotas, budgets, and approvals",
    "Versioned rollout, evaluation, and kill switch",
  ],
  data: [
    "JWT validation and actor/team attribution",
    "Policy enforcement and request routing",
    "ACL-filtered retrieval and model invocation",
    "Typed tool authorization, execution, and telemetry",
  ],
};
